<script lang="ts">
  import { colorMode, visualTheme, isDark, type ColorMode } from '$stores/theme.js';
  import { loadCustomThemeCss } from '$lib/themeLoader.js';

  const colorModes: { value: ColorMode; label: string }[] = [
    { value: 'auto', label: __STRINGS__.theme.modeAuto },
    { value: 'light', label: __STRINGS__.theme.modeLight },
    { value: 'dark', label: __STRINGS__.theme.modeDark },
  ];

  // Theme list is fully driven by content/config.json via the __THEME_LIST__ build constant.
  const visualThemes: { value: string; label: string; desc: string }[] = __THEME_LIST__.map(
    (t) => ({ value: t.id, label: t.label, desc: t.desc }),
  );

  let showDropdown = false;

  function toggleDropdown() {
    showDropdown = !showDropdown;
  }

  function closeDropdown() {
    showDropdown = false;
  }

  function selectColorMode(mode: ColorMode) {
    colorMode.set(mode);
  }

  function selectVisualTheme(id: string) {
    visualTheme.set(id);
    loadCustomThemeCss(id, __THEME_LIST__);
  }
</script>

<svelte:window on:click={closeDropdown} />

<div class="theme-switcher">
  <button
    class="theme-button"
    on:click|stopPropagation={toggleDropdown}
    aria-label={__STRINGS__.theme.settingsLabel}
    aria-expanded={showDropdown}
  >
    {#if $isDark}
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z"/>
      </svg>
    {:else}
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <circle cx="12" cy="12" r="5"/>
        <line x1="12" y1="1" x2="12" y2="3"/>
        <line x1="12" y1="21" x2="12" y2="23"/>
        <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/>
        <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
        <line x1="1" y1="12" x2="3" y2="12"/>
        <line x1="21" y1="12" x2="23" y2="12"/>
        <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/>
        <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
      </svg>
    {/if}
  </button>

  {#if showDropdown}
    <!-- svelte-ignore a11y_click_events_have_key_events a11y_no_static_element_interactions -->
    <div class="dropdown" on:click|stopPropagation>
      <div class="section">
        <h4>{__STRINGS__.theme.colorModeHeading}</h4>
        <div class="options">
          {#each colorModes as mode}
            <button
              class="option"
              class:active={$colorMode === mode.value}
              on:click={() => selectColorMode(mode.value)}
            >
              {mode.label}
            </button>
          {/each}
        </div>
      </div>

      <div class="section">
        <h4>{__STRINGS__.theme.visualThemeHeading}</h4>
        <div class="theme-options">
          {#each visualThemes as theme}
            <button
              class="theme-option"
              class:active={$visualTheme === theme.value}
              on:click={() => selectVisualTheme(theme.value)}
            >
              <span class="theme-name">{theme.label}</span>
              <span class="theme-desc">{theme.desc}</span>
            </button>
          {/each}
        </div>
      </div>
    </div>
  {/if}
</div>

<style>
  .theme-switcher {
    position: relative;
  }

  .theme-button {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    background: none;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    color: var(--color-menubar-text, #fff);
    opacity: 0.85;
    transition: opacity 0.15s, background 0.15s;
  }

  .theme-button:hover {
    opacity: 1;
    background: var(--color-menubar-hover, rgba(255, 255, 255, 0.1));
  }

  .dropdown {
    position: absolute;
    top: 100%;
    right: 0;
    margin-top: 0.5rem;
    min-width: 200px;
    background: var(--color-surface, #fff);
    border: 1px solid var(--color-border, #e0e0e0);
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    z-index: 200;
  }

  .section {
    padding: 0.75rem;
  }

  .section + .section {
    border-top: 1px solid var(--color-border, #e0e0e0);
  }

  h4 {
    margin: 0 0 0.5rem;
    font-size: 0.7rem;
    font-weight: 600;
    color: var(--color-text-muted, #666);
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  .options {
    display: flex;
    gap: 0.25rem;
  }

  .option {
    flex: 1;
    padding: 0.4rem 0.6rem;
    background: var(--color-surface-hover, #f5f5f5);
    border: 1px solid transparent;
    border-radius: 4px;
    font-size: 0.85rem;
    color: var(--color-text, #333);
    cursor: pointer;
    transition: border-color 0.15s, background 0.15s;
  }

  .option:hover {
    background: var(--color-surface, #fff);
    border-color: var(--color-border, #e0e0e0);
  }

  .option.active {
    background: var(--color-primary, #e94560);
    color: white;
    border-color: var(--color-primary, #e94560);
  }

  .theme-options {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  .theme-option {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    padding: 0.5rem 0.75rem;
    background: var(--color-surface-hover, #f5f5f5);
    border: 1px solid transparent;
    border-radius: 4px;
    cursor: pointer;
    transition: border-color 0.15s, background 0.15s;
  }

  .theme-option:hover {
    background: var(--color-surface, #fff);
    border-color: var(--color-border, #e0e0e0);
  }

  .theme-option.active {
    border-color: var(--color-primary, #e94560);
    background: var(--color-surface, #fff);
  }

  .theme-name {
    font-size: 0.9rem;
    font-weight: 500;
    color: var(--color-text, #333);
  }

  .theme-desc {
    font-size: 0.75rem;
    color: var(--color-text-muted, #666);
  }

  .theme-option.active .theme-name {
    color: var(--color-primary, #e94560);
  }
</style>
